import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Pot } from '../types';
import { formatCurrency } from '../utils/calculations';

const PotManager: React.FC = () => {
  const { state } = useAppContext();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPot, setEditingPot] = useState<Pot | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Pot Management</h2>
          <div className="flex space-x-1"></div>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary"
          >
            Add Pot
          </button>
        </div>

        {state.pots.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No pots configured. Create your first pot to get started!
          </div>
        ) : (
          <div className="table-container pots-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text-left">Pot Name</th>
                  <th className="text-right">Current Balance</th>
                  <th className="text-right">Target Amount</th>
                  <th className="text-center">Progress</th>
                  <th className="text-center">Type</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.pots.map(pot => (
                  <PotTableRow key={pot.id} pot={pot} onEdit={() => setEditingPot(pot)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddForm && (
        <AddPotModal onClose={() => setShowAddForm(false)} />
      )}

      {editingPot && (
        <EditPotModal pot={editingPot} onClose={() => setEditingPot(null)} />
      )}
    </div>
  );
};

interface PotTableRowProps {
  pot: Pot;
  onEdit: () => void;
}

const PotTableRow: React.FC<PotTableRowProps> = ({ pot, onEdit }) => {
  const { dispatch } = useAppContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editBalance, setEditBalance] = useState(pot.currentBalance.toString());

  const handleSaveBalance = () => {
    const newBalance = parseFloat(editBalance);
    if (!isNaN(newBalance)) {
      dispatch({
        type: 'UPDATE_POT',
        payload: { id: pot.id, pot: { currentBalance: newBalance } }
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditBalance(pot.currentBalance.toString());
    setIsEditing(false);
  };

  const deletePot = () => {
    if (window.confirm(`Are you sure you want to delete the "${pot.name}" pot?`)) {
      dispatch({ type: 'DELETE_POT', payload: pot.id });
    }
  };

  const progressPercentage = pot.targetAmount 
    ? Math.min((pot.currentBalance / pot.targetAmount) * 100, 100)
    : 0;

  return (
    <tr>
      <td className="text-left">
        <div className="flex items-center">
          <div
            className="w-4 h-4 rounded-full border border-gray-400 mr-2 flex-shrink-0"
            style={{ backgroundColor: pot.color, minWidth: '16px', minHeight: '16px' }}
          />
          <span className="font-medium text-gray-800">{pot.name}</span>
        </div>
      </td>
      <td className="text-right">
        {isEditing ? (
          <>
            <input
              type="number"
              step="0.01"
              value={editBalance}
              onChange={(e) => setEditBalance(e.target.value)}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
            />
            <button
              onClick={handleSaveBalance}
              className="px-2 py-1 btn-secondary text-xs mr-1"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-2 py-1 btn-danger text-xs"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="font-bold text-green-600 mr-2">
              {formatCurrency(pot.currentBalance)}
            </span>
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary text-xs px-2 py-1"
            >
              Edit
            </button>
          </>
        )}
      </td>
      <td className="text-right text-gray-600">
        {pot.targetAmount ? formatCurrency(pot.targetAmount) : '-'}
      </td>
      <td className="text-center">
        {pot.targetAmount ? (
          <div>
            <div className="text-sm text-gray-600 mb-1">{progressPercentage.toFixed(0)}%</div>
            <div className="w-16 bg-gray-200 rounded-full h-2 mx-auto">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${progressPercentage}%`,
                  backgroundColor: pot.color
                }}
              />
            </div>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="text-center">
        <span className={`pill ${
          pot.trackingType === 'cumulative'
            ? 'pill-info'
            : 'pill-warning'
        }`}>
          {pot.trackingType === 'cumulative' ? '📈 Cumulative' : '🔄 Resets Monthly'}
        </span>
      </td>
      <td className="text-center">
        <button
          onClick={onEdit}
          className="btn-secondary text-xs px-2 py-1 mr-2"
        >
          Edit
        </button>
        <button
          onClick={deletePot}
          className="btn-danger text-xs px-2 py-1"
        >
          Delete
        </button>
      </td>
    </tr>
  );
};

interface AddPotModalProps {
  onClose: () => void;
}

interface EditPotModalProps {
  pot: Pot;
  onClose: () => void;
}

const AddPotModal: React.FC<AddPotModalProps> = ({ onClose }) => {
  const { dispatch } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    currentBalance: '0',
    targetAmount: '',
    trackingType: 'cumulative' as 'cumulative' | 'resetting',
    color: '#DC2626'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const pot: Omit<Pot, 'id'> = {
      name: formData.name,
      currentBalance: parseFloat(formData.currentBalance),
      targetAmount: formData.targetAmount ? parseFloat(formData.targetAmount) : undefined,
      trackingType: formData.trackingType,
      color: formData.color
    };

    dispatch({ type: 'ADD_POT', payload: pot });
    onClose();
  };

  const colorOptions = [
    '#DC2626', // Red
    '#F97316', // Orange
    '#EAB308', // Yellow
    '#22C55E', // Green
    '#06B6D4', // Cyan
    '#3B82F6', // Blue
    '#8B5CF6', // Purple
    '#EC4899', // Pink
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Add New Pot</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pot Name
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Emergency Fund, Holiday Savings"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Balance (£)
            </label>
            <input
              type="number"
              step="0.01"
              required
              value={formData.currentBalance}
              onChange={(e) => setFormData({ ...formData, currentBalance: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Amount (£) - Optional
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.targetAmount}
              onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Leave empty for no target"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tracking Type
            </label>
            <select
              value={formData.trackingType}
              onChange={(e) => setFormData({ ...formData, trackingType: e.target.value as 'cumulative' | 'resetting' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="cumulative">Cumulative (balance carries over)</option>
              <option value="resetting">Resets Monthly (resets to 0 each month)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="grid grid-cols-8 gap-2">
              {colorOptions.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color ? 'border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 btn-primary"
            >
              Add Pot
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-danger"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditPotModal: React.FC<EditPotModalProps> = ({ pot, onClose }) => {
  const { dispatch } = useAppContext();
  const [formData, setFormData] = useState({
    name: pot.name,
    currentBalance: pot.currentBalance.toString(),
    targetAmount: pot.targetAmount?.toString() || '',
    trackingType: pot.trackingType,
    color: pot.color
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updatedPot: Partial<Pot> = {
      name: formData.name,
      currentBalance: parseFloat(formData.currentBalance),
      targetAmount: formData.targetAmount ? parseFloat(formData.targetAmount) : undefined,
      trackingType: formData.trackingType,
      color: formData.color
    };

    dispatch({ type: 'UPDATE_POT', payload: { id: pot.id, pot: updatedPot } });
    onClose();
  };

  const colorOptions = [
    '#DC2626', // Red
    '#F97316', // Orange
    '#EAB308', // Yellow
    '#22C55E', // Green
    '#06B6D4', // Cyan
    '#3B82F6', // Blue
    '#8B5CF6', // Purple
    '#EC4899', // Pink
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Edit Pot</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pot Name
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Emergency Fund, Holiday Savings"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Balance (£)
            </label>
            <input
              type="number"
              step="0.01"
              required
              value={formData.currentBalance}
              onChange={(e) => setFormData({ ...formData, currentBalance: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Amount (£) - Optional
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.targetAmount}
              onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Leave empty for no target"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tracking Type
            </label>
            <select
              value={formData.trackingType}
              onChange={(e) => setFormData({ ...formData, trackingType: e.target.value as 'cumulative' | 'resetting' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="cumulative">Cumulative (balance carries over)</option>
              <option value="resetting">Resets Monthly (resets to 0 each month)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="grid grid-cols-8 gap-2">
              {colorOptions.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color ? 'border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 btn-primary"
            >
              Update Pot
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-danger"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PotManager;